import { FC, useState, useTransition } from 'react'
import {
  Affix,
  Alert,
  Button,
  Drawer,
  Indicator,
  LoadingOverlay,
  Paper,
  Popover,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core'
import { isEmpty } from 'lodash-es'
import { TbListDetails, TbRoad, TbTools } from 'react-icons/tb'
import { useTranslation } from 'react-i18next'
import { CLEAR_MAP_OVERLAY_PROPS } from './constants'
import { Itinerary } from './Itinerary'
import { PlannerStatusBanners } from './PlannerStatusBanners'
import { PathLength, RoutePlannerToolbar } from './RoutePlannerToolbar'
import { RoutePlannerMap } from './RoutePlannerMap'
import { VersionWarningModal } from './VersionWarningModal'
import { RoutePlannerHelp } from './Help'
import { hasRemovalGapMarkers } from './saveGate'
import { shouldConfirmSchemaSwitch, type PlannerControlSchema } from './schemaSwitch'
import { initialInteractionMode, type RouteSelectionChangeMeta } from './createModePolicy'
import { useRoutePlannerLogic } from './useRoutePlannerLogic'
import { usePlannerDisplaySettings } from './usePlannerDisplaySettings'
import { usePlannerMode } from './usePlannerMode'
import { usePlannerHintsAndGaps } from './usePlannerHintsAndGaps'
import { usePlannerInteractions } from './usePlannerInteractions'
import { usePlannerMapData } from './usePlannerMapData'
import { usePlannerLeaveGuard } from './usePlannerLeaveGuard'
import type { InteractionMode, RouteId } from './types'

export type { RouteSelectionChangeMeta }

type RoutePlannerProps = {
  areaId: string
  initialRoute?: RouteId
  initialCreateMode?: boolean
  returnRouteAfterCreate?: RouteId
  onRouteSelectionChange?: (route: RouteId | undefined, meta?: RouteSelectionChangeMeta) => void
}

export const RoutePlanner: FC<RoutePlannerProps> = ({
  areaId,
  initialRoute,
  initialCreateMode = false,
  returnRouteAfterCreate,
  onRouteSelectionChange,
}) => {
  const { t } = useTranslation('routePlanner')
  const display = usePlannerDisplaySettings()
  const planner = useRoutePlannerLogic(
    areaId,
    initialRoute,
    initialInteractionMode(Boolean(initialRoute), initialCreateMode),
    display.controlDataSchema
  )

  const [pendingSchema, setPendingSchema] = useState<PlannerControlSchema | null>(null)
  const [routeStatsOpen, setRouteStatsOpen] = useState(false)
  // The schema switch changes a suspense-query key. Applying it in a transition
  // keeps the current UI (and the mapbox instance — recreating it loses the
  // camera) alive while the new planning data loads.
  const [isSchemaSwitchPending, startSchemaTransition] = useTransition()
  const applyControlDataSchema = (schema: PlannerControlSchema) => {
    startSchemaTransition(() => {
      display.setControlDataSchema(schema)
    })
  }
  const requestControlDataSchema = (schema: PlannerControlSchema) => {
    if (
      shouldConfirmSchemaSwitch({
        fromSchema: display.controlDataSchema,
        toSchema: schema,
        hasUnsavedChanges: planner.hasUnsavedChanges,
        hasEditState: Boolean(planner.editState),
      })
    ) {
      setPendingSchema(schema)
      return
    }
    applyControlDataSchema(schema)
  }

  const leave = usePlannerLeaveGuard({
    hasUnsavedChanges: planner.hasUnsavedChanges,
  })

  const mode = usePlannerMode({
    initialRoute,
    initialCreateMode,
    returnRouteAfterCreate,
    interactionMode: planner.interactionMode,
    setInteractionMode: planner.setInteractionMode,
    selectedRoute: planner.selectedRoute,
    setSelectedRoute: planner.setSelectedRoute,
    existingRoutes: planner.existingRoutes,
    editState: planner.editState,
    pathState: planner.pathState,
    hasUnsavedChanges: leave.hasUnsavedChanges,
    suppressUnsavedFlag: leave.suppressUnsavedFlag,
    resetPathHistory: planner.resetPathHistory,
    onRouteSelectionChange,
    onClearTransient: planner.clearTransients,
  })

  const hints = usePlannerHintsAndGaps({
    isDeleteMode: mode.isDeleteMode,
    isPathBuildMode: mode.isPathBuildMode,
    pathState: planner.pathState,
    pathNodes: planner.pathNodes,
    editState: planner.editState,
    addTarget: planner.addTarget,
    pendingRemoval: planner.pendingRemoval,
    setGapFixPreview: planner.setGapFixPreview,
    nonBlockedStreetSegments: planner.nonBlockedStreetSegments,
    indexedSegments: planner.indexedSegments,
    streetNodes: planner.streetNodes,
  })

  const interactions = usePlannerInteractions({
    routeExists: planner.routeExists,
    selectedRouteId: planner.selectedRoute?.id,
    selectedRouteVersion: planner.selectedRoute?.version,
    pathState: planner.pathState,
    pathNodes: planner.pathNodes,
    editState: planner.editState,
    canUndoSegment: planner.canUndoSegment,
    streetNodes: planner.streetNodes,
    continuationSegments: planner.continuationSegments,
    indexedSegments: planner.indexedSegments,
    nonBlockedStreetSegments: planner.nonBlockedStreetSegments,
    appendToPath: planner.appendToPath,
    deleteLastSegment: planner.deleteLastSegment,
    startEdit: planner.startEdit,
    cancelEdit: planner.cancelEdit,
    commitEdit: planner.commitEdit,
    canCommitEdit: planner.canCommitEdit,
    acceptTail: planner.acceptTail,
    canAcceptTail: planner.canAcceptTail,
    acceptShortestPath: planner.acceptShortestPath,
    removeLoop: planner.removeLoop,
    applyGapFixes: planner.applyGapFixes,
    isDeleteMode: mode.isDeleteMode,
    isPathBuildMode: mode.isPathBuildMode,
    addTarget: planner.addTarget,
    setAddTarget: planner.setAddTarget,
    pendingRemoval: planner.pendingRemoval,
    setPendingRemoval: planner.setPendingRemoval,
    activePicker: planner.activePicker,
    setActivePicker: planner.setActivePicker,
    removalPlan: hints.removalPlan,
    shortestPathHint: hints.shortestPathHint,
    hintReconnectNodeId: hints.hintReconnectNodeId,
    gapFixes: hints.gapFixes,
    applyRemovalAtIndex: planner.applyRemovalAtIndex,
    t,
  })

  const mapData = usePlannerMapData({
    areaId,
    planningAreaBounds: planner.planningArea.bounds,
    polygon: planner.polygon,
    pathState: planner.pathState,
    pathNodes: planner.pathNodes,
    editState: planner.editState,
    indexedSegments: planner.indexedSegments,
    indexedAllSegments: planner.indexedAllSegments,
    streetNodes: planner.streetNodes,
    signPosts: planner.signPosts,
    technicalSegments: planner.technicalSegments,
    continuationSegments: planner.continuationSegments,
    nonBlockedStreetSegments: planner.nonBlockedStreetSegments,
    drivesBySegment: planner.drivesBySegment,
    displayAllNodes: display.displayAllNodes,
    displayOffNetworkNodes: display.displayOffNetworkNodes,
    displaySignPosts: display.displaySignPosts,
    displayParkingAreas: display.displayParkingAreas,
    isPathBuildMode: mode.isPathBuildMode,
    addTarget: planner.addTarget,
    shortestPathHint: hints.shortestPathHint,
    hintReconnectNodeId: hints.hintReconnectNodeId,
    hintVisitByNode: hints.hintVisitByNode,
    hintReconnectPosition: hints.hintReconnectPosition,
    hintNodeIds: hints.hintNodeIds,
    pendingNodeIds: hints.pendingNodeIds,
    routeGapNodeIds: hints.routeGapNodeIds,
    gapFixes: hints.gapFixes,
    gapFixPreview: planner.gapFixPreview,
    reconnectIndex: hints.reconnectIndex,
  })

  const hasBlockingGaps = hints.routeGapNodeIds.size > 0 || hasRemovalGapMarkers(planner.pathState.segments)

  const showCancelChanges =
    leave.hasUnsavedChanges ||
    Boolean(planner.editState) ||
    (mode.interactionMode === 'create' && !isEmpty(planner.pathState.segments))

  return (
    <div style={{ position: 'relative', width: '100%', height: '100vh' }}>
      <LoadingOverlay
        visible={planner.isRoutePlanLoading || isSchemaSwitchPending}
        overlayProps={CLEAR_MAP_OVERLAY_PROPS}
      />
      <RoutePlannerMap
        initialBounds={mapData.initialBounds}
        routeFitKey={planner.routeFitKey}
        pathSegmentCount={planner.pathState.segments.length}
        areaPolygon={mapData.areaPolygon}
        parkingPolygons={mapData.parkingPolygonsGeo}
        streetSegments={mapData.streetSegmentsGeo}
        pathSegments={mapData.pathSegmentsGeo}
        continuationSegments={mapData.continuationGeo}
        shortestPathSegments={mapData.shortestPathGeo}
        gapFixSegments={mapData.gapFixGeo}
        editTailSegments={mapData.editTailGeo}
        signPosts={mapData.signPostsGeo}
        signPostAzimuths={mapData.signPostAzimuthsGeo}
        displaySignPosts={display.displaySignPosts}
        displayParkingAreas={display.displayParkingAreas}
        displayNodeNumbers={display.displayNodeNumbers}
        nodes={mapData.nodesGeo}
        onNodeClick={interactions.handleNodeClick}
        onContinuationClick={interactions.handleContinuationClick}
        onBackgroundClick={interactions.handleMapBackgroundClick}
        onMapMoveStart={interactions.clearActivePicker}
        onViewportBoundsChange={mapData.setViewportBounds}
        onZoomChange={mapData.setZoom}
      />

      <Popover
        opened={interactions.activePicker !== null}
        onClose={interactions.clearActivePicker}
        closeOnClickOutside
        closeOnEscape
        position="top"
        withArrow
        shadow="md"
        withinPortal
        middlewares={{ flip: true, shift: true }}
      >
        <Popover.Target>
          <div
            style={{
              position: 'fixed',
              left: interactions.activePicker?.x ?? 0,
              top: interactions.activePicker?.y ?? 0,
              width: 1,
              height: 1,
              pointerEvents: 'none',
            }}
          />
        </Popover.Target>
        <Popover.Dropdown bg="var(--mantine-color-body)">
          <Stack gap="xs">
            <Text size="xs" fw={600}>
              {interactions.activePicker?.action === 'edit' ? t('continueFromVisit') : t('removeVisit')}
            </Text>
            {interactions.activePicker?.visits.map(pos => (
              <Button
                key={pos}
                size="xs"
                variant="light"
                color={interactions.activePicker?.action === 'delete' ? 'red' : 'blue'}
                onClick={() => interactions.handleVisitPickerSelect(pos)}
              >
                {t('visitNumber', { n: pos })}
              </Button>
            ))}
          </Stack>
        </Popover.Dropdown>
      </Popover>

      <Affix position={{ top: 16, left: '50%' }} style={{ transform: 'translateX(-50%)' }} zIndex={400}>
        <Paper shadow="md" radius="md" p={4}>
          <SegmentedControl
            size="sm"
            value={mode.interactionMode}
            onChange={value => mode.requestInteractionMode(value as InteractionMode)}
            data={[
              { label: t('modeEdit'), value: 'edit' },
              { label: t('modeCreate'), value: 'create' },
              { label: t('modeDelete'), value: 'delete' },
            ]}
          />
        </Paper>
      </Affix>

      {!display.drawerOpen && (
        <Affix position={{ top: 16, right: 16 }} zIndex={400}>
          <Indicator color="orange" size={10} disabled={display.controlDataSchema === 'vehicle'}>
            <Button
              radius="xl"
              variant="default"
              leftSection={<TbTools size={18} aria-hidden />}
              onClick={display.drawerHandlers.open}
            >
              {t('tools')}
            </Button>
          </Indicator>
        </Affix>
      )}

      {!display.displayLegend && (
        <Affix position={{ bottom: 16, left: 16 }} zIndex={400}>
          <Button
            radius="xl"
            variant="default"
            leftSection={<TbListDetails size={18} aria-hidden />}
            onClick={() => display.setDisplayLegend(true)}
          >
            {t('displayLegend')}
          </Button>
        </Affix>
      )}

      <PlannerStatusBanners
        t={t}
        isDeleteMode={mode.isDeleteMode}
        isPathBuildMode={mode.isPathBuildMode}
        pendingRemoval={planner.pendingRemoval}
        removalPlan={hints.removalPlan}
        onConfirmRemoval={interactions.confirmRemoval}
        shortestPathHint={hints.shortestPathHint}
        editState={planner.editState}
        addTarget={planner.addTarget}
        setAddTarget={planner.setAddTarget}
        onAcceptShortestPath={() =>
          hints.shortestPathHint && interactions.acceptShortestPathWithHistory(hints.shortestPathHint)
        }
        onCommitEdit={interactions.commitEditWithHistory}
        onCancelEdit={interactions.cancelEditWithCleanup}
        routableNodeIds={mapData.routableNodeIds}
        gapFixes={hints.gapFixes}
        gapFixPreview={planner.gapFixPreview}
        setGapFixPreview={planner.setGapFixPreview}
        onApplyGapFixes={interactions.applyGapFixes}
      />

      <Drawer
        opened={display.displayLegend}
        onClose={() => display.setDisplayLegend(false)}
        position="left"
        size={400}
        title={t('legendTitle')}
        padding="md"
        withOverlay={false}
      >
        <RoutePlannerHelp />
      </Drawer>

      <Drawer
        opened={display.drawerOpen}
        onClose={display.drawerHandlers.close}
        position="right"
        size="md"
        title={planner.planningArea.name ?? areaId}
        padding="md"
        withOverlay={false}
        radius={0}
      >
        <Stack gap="md">
          {planner.routeLoadError && (
            <Alert color="red" title={t('routeLoadErrorTitle')}>
              <Text size="sm">{t('routeLoadErrorMessage')}</Text>
            </Alert>
          )}
          <RoutePlannerToolbar
            path={planner.pathState}
            onDeleteLastSegment={interactions.deleteLastSegmentGuarded}
            canUndoLastSegment={planner.canUndoSegment}
            onUndoLastOperation={planner.undoOperation}
            canUndoOperation={planner.canUndoOperation}
            onSelectRoute={mode.handleSelectRoute}
            onRouteSaved={planner.adoptSavedPlan}
            onCancelChanges={
              showCancelChanges
                ? () => {
                    if (planner.editState) planner.cancelEdit()
                    if (mode.interactionMode === 'create') {
                      planner.clearPlanState()
                    } else {
                      planner.resetToOriginal()
                    }
                    planner.clearTransients()
                  }
                : null
            }
            selectedRoute={planner.selectedRoute}
            existingRoutes={planner.existingRoutes}
            areaId={areaId}
            displayAllNodes={display.displayAllNodes}
            setDisplayAllNodes={display.setDisplayAllNodes}
            displayOffNetworkNodes={display.displayOffNetworkNodes}
            setDisplayOffNetworkNodes={display.setDisplayOffNetworkNodes}
            displaySignPosts={display.displaySignPosts}
            setDisplaySignPosts={display.setDisplaySignPosts}
            displayParkingAreas={display.displayParkingAreas}
            setDisplayParkingAreas={display.setDisplayParkingAreas}
            displayNodeNumbers={display.displayNodeNumbers}
            setDisplayNodeNumbers={display.setDisplayNodeNumbers}
            controlDataSchema={display.controlDataSchema}
            onControlDataSchemaChange={requestControlDataSchema}
            routeExists={planner.routeExists}
            routeLoadError={planner.routeLoadError}
            createMode={mode.interactionMode === 'create'}
            onRequestSaveConfirm={interactions.requestSaveConfirm}
            hasActiveEdit={Boolean(planner.editState)}
            hasBlockingGaps={hasBlockingGaps}
          />
          <Button
            variant="default"
            fullWidth
            leftSection={<TbRoad size={16} aria-hidden />}
            onClick={() => setRouteStatsOpen(true)}
          >
            {t('routeStatsButton', { count: planner.effectivePathState.segments.length })}
          </Button>
        </Stack>
      </Drawer>

      <Drawer
        opened={routeStatsOpen}
        onClose={() => setRouteStatsOpen(false)}
        position="right"
        size="md"
        title={t('routeStatsTitle')}
        padding="md"
        withOverlay={false}
        radius={0}
      >
        {routeStatsOpen && (
          <Stack gap="md">
            <PathLength
              path={planner.effectivePathState}
              segments={planner.indexedSegments}
              drivesBySegment={planner.drivesBySegment}
            />
            <Itinerary path={planner.effectivePathState} segments={planner.indexedAllSegments} />
          </Stack>
        )}
      </Drawer>

      <VersionWarningModal
        opened={interactions.editAckOpen}
        title={t('editAckTitle')}
        message={t('editAckMessage')}
        onCancel={interactions.cancelEditAck}
        onConfirm={interactions.confirmEditAck}
      />

      <VersionWarningModal
        opened={interactions.saveConfirmOpen}
        title={t('saveConfirmTitle')}
        message={t('saveConfirmMessage')}
        onCancel={interactions.cancelSave}
        onConfirm={interactions.confirmSave}
      />

      <VersionWarningModal
        opened={leave.leaveBlocker.status === 'blocked'}
        title={t('unsavedChangesTitle')}
        message={t('unsavedChangesMessage')}
        confirmLabel={t('leaveWithoutSaving')}
        onCancel={() => leave.leaveBlocker.reset?.()}
        onConfirm={() => leave.leaveBlocker.proceed?.()}
      />

      <VersionWarningModal
        opened={pendingSchema !== null}
        title={t('schemaSwitchTitle')}
        message={t('schemaSwitchMessage')}
        confirmLabel={t('continue')}
        onCancel={() => setPendingSchema(null)}
        onConfirm={() => {
          if (pendingSchema) applyControlDataSchema(pendingSchema)
          setPendingSchema(null)
        }}
      />

      <VersionWarningModal
        opened={mode.modeSwitchConfirmOpen}
        title={t('modeSwitchUnsavedTitle')}
        message={mode.pendingMode === 'create' ? t('modeSwitchToCreateMessage') : t('modeSwitchUnsavedMessage')}
        confirmLabel={t('modeSwitchDiscardContinue')}
        onCancel={mode.cancelModeSwitch}
        onConfirm={mode.confirmModeSwitch}
      />
    </div>
  )
}

export default RoutePlanner
